import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { OrderShuttleVM } from '../../../models/orderModel';
import { OrderShuttleService } from '../../../services/ordershuttle.service';
import { AdminService } from '../../../services/admin.service';
import { DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HotelShuttleServiceEditComponent } from './hotel-shuttle-service-edit/hotel-shuttle-service-edit.component';

@Component({
  selector: 'app-hotel-shuttle-service-list',
  templateUrl: './hotel-shuttle-service-list.component.html',
  styleUrls: ['./hotel-shuttle-service-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class HotelShuttleServiceListComponent implements OnInit, OnDestroy {
  organizationId: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  excludeCancelled: boolean;
  orderShuttleForm: UntypedFormGroup;
  orderShuttles: OrderShuttleVM[];
  dataSource: MatTableDataSource<OrderShuttleVM> = new MatTableDataSource<OrderShuttleVM>([]);
  isLoading = false;

  filtersExpanded: boolean = true;

  displayedColumns: string[] = [
    'orderNr',
    'firstName',
    'cellular',
    'email',
    'passengers',
    'shuttleDate',
    'shuttleTypeName',
    'busStartDate',
    'busStatus',
    'flightNr',
    'terminal',
    // 'boardedTime',
    'status'
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  componentDestroyed$: Subject<boolean> = new Subject();

  constructor(
    public orderShuttleService: OrderShuttleService,
    public adminService: AdminService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.initShuttles();
  }

  initShuttles() {
    this.dateFrom = moment().format();
    this.timeFrom = "00:00";
    this.dateTo = moment().add(10, 'd').format();
    this.timeTo = "00:00";
    this.excludeCancelled = true;
    this.initOrderShuttleForm();
    this.getOrganizationidId();
  }

  getOrganizationidId() {
    this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
      this.organizationId = params['organizationId'];
      this.getShuttles();
    });
  }

  initOrderShuttleForm() {
    this.orderShuttleForm = this.fb.group({
      dateFrom: [this.dateFrom],
      dateTo: [this.dateTo],
      timeFrom: [this.timeFrom],
      timeTo: [this.timeTo],
      excludeCancelled: [this.excludeCancelled]
    });
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  clearFilters(): void {
    this.orderShuttleForm.patchValue({
      excludeCancelled: true
    });
  }

  getShuttles() {
    this.isLoading = true;
    this.setDateFormats();
    this.orderShuttles = [];
    this.orderShuttleService
      .getOrderShuttlesPaidOrganizationBetweenShuttleDate(this.organizationId, this.dateFrom, this.dateTo)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (orderShuttles) => {
          orderShuttles.forEach(orderShuttle => {
            orderShuttle.firstName = orderShuttle.firstName + ' ' + orderShuttle.lastName;

            orderShuttle.boardedTime = orderShuttle.status === "Boarded" ? moment(orderShuttle.shuttleUpdated).format('YYYY-MM-DD HH:mm') : null;

            const formValues = this.orderShuttleForm.value;
            if(orderShuttle.status !== 'Cancelled') {
              this.orderShuttles.push(orderShuttle);
            }
            if(orderShuttle.status === 'Cancelled' && !formValues.excludeCancelled) {
              this.orderShuttles.push(orderShuttle);
            }
          });

          this.dataSource = new MatTableDataSource<OrderShuttleVM>(this.orderShuttles);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.snackBar.open(`Error: ${error.status}`, 'OK', {
            verticalPosition: 'top'
          });
          console.error('HotelShuttleServiceListComponent - Error in getShuttles', error);
        }
      });
  }

  setDateFormats() {
    this.dateFrom = this.datepipe.transform(this.orderShuttleForm.controls['dateFrom'].value, 'yyyy-MM-dd') + ' ' + this.orderShuttleForm.controls['timeFrom'].value;
    this.dateTo = this.datepipe.transform(this.orderShuttleForm.controls['dateTo'].value, 'yyyy-MM-dd') + ' '  + this.orderShuttleForm.controls['timeTo'].value;
  }

  valueChange($event) {
    this.excludeCancelled = $event.checked;
    this.orderShuttleForm.patchValue({
      excludeCancelled: this.excludeCancelled
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  flatten(obj: any) {
    return Object.keys(obj).reduce((acc, current) => {
      const _key = `${current}`;
      const currentValue = obj[current];
      if (Array.isArray(currentValue) === currentValue) {
        Object.assign(acc, this.flatten(currentValue));
      } else {
        acc[_key] = currentValue;
      }
      return acc;
    }, {});
  }

  goToOrder(orderId: string): void {
    this.dialog.open(HotelShuttleServiceEditComponent, {
      height: 'auto',
      width: '720px',
      hasBackdrop: true,
      data: { "orderId": orderId }
    });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
